import React, { useEffect, useState } from "react";
import axios from "axios";
import "./App.scss";
import "./components/components.scss";
import "./containers/containers.scss";
import "./containers/pageContents/pageContent.scss";
import "./components/staticComponents/popups.scss";
import "./components/staticComponents/HighlightNotes.scss";
import Banner from "./containers/Banner";
import MainContent from "./containers/MainContent";
import PasswordInput from "./containers/PasswordInput";
import Dashboard from "./containers/Dashboard";
import LockedNotice from "./containers/LockedNotice";
import { watchForLeave } from "./utils/leaveGuard";
import {
  getGroupHint,
  getGroupTypeMapping,
  parseAnswers,
  parseGroupAnswerContent,
  questionReg,
  questionsReg,
} from "./containers/pageContents/Const";

const App = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [urlKey, setUrlKey] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [examContent, setExamContent] = useState(null);
  const [testPages, setTestPages] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [testType, setTestType] = useState("L");
  const [testTime, setTestTime] = useState(0);
  const [pageType, setPageType] = useState("Intro");
  const [pageNumber, setPageNumber] = useState(1);
  const [playTestSound, setPlayTestSound] = useState(false);
  const [testEnd, setTestEnd] = useState(false);
  // 連結種類：dashboard＝一場模考的總連結／section＝單科連結（舊信裡的）
  const [linkKind, setLinkKind] = useState("section");
  // 總連結的密碼記在這裡：考完一科回到科目頁時不用再輸一次。
  // 只存在記憶體，重新整理就沒了（那時本來就該重新驗證）。
  const [dashboardPassword, setDashboardPassword] = useState("");
  // 閱讀／寫作頁的左右欄比例與字級。放在這裡是為了讓考生調過一次之後，
  // 換頁、甚至換科目都維持同一個設定，不必每頁重調。
  const [splitRatio, setSplitRatio] = useState(50);
  const [fontScale, setFontScale] = useState(1);
  // 正式模考中途離開被鎖住時，整個畫面換成說明頁
  const [locked, setLocked] = useState(null);

  // console.log(answers);

  useEffect(() => {
    let path = window.location.pathname;

    const url = process.env.REACT_APP_IS_URLKEY_VALID + path;
    axios.get(`${url}`).then((res) => {
      const data = res.data;
      if (data.success) {
        setUrlKey(data.urlKey);
        setLinkKind(data.kind === "dashboard" ? "dashboard" : "section");
      } else {
        if (process.env.NODE_ENV === "development") {
          return;
        } else {
          window.location.href = process.env.REACT_APP_HOMEPAGE;
        }
      }
    });
  }, []);

  /**
   * 正式模考的中途離開偵測。
   *
   * 只在「已進入某一科、還沒交卷、而且是正式模考」時才監看；
   * 自我練習不鎖（伺服器端也會再判斷一次模式）。
   */
  useEffect(() => {
    if (!isLoaded || !candidate || !candidate.examDetailId) {
      return undefined;
    }
    if (candidate.mode === "practice") {
      return undefined;
    }
    if (testEnd || pageType === "End" || locked) {
      return undefined;             // 已交卷，關掉頁面是正常行為
    }

    return watchForLeave({
      examDetailId: candidate.examDetailId,
      onLocked: (info) => setLocked(info),
    });
  }, [isLoaded, candidate, testEnd, pageType, locked]);

  const mainContent = () => {
    if (isLoaded) {
      return (
        <MainContent
          candidate={candidate}
          testPages={testPages}
          answers={answers}
          testType={testType}
          pageType={pageType}
          pageNumber={pageNumber}
          renderNextPage={renderNextPage}
          renderPreviousPage={renderPreviousPage}
          updatePlayTestSound={updatePlayTestSound}
          testSoundBtn={playTestSound ? "Stop sound" : "Play sound"}
          TestsPageJump={TestsPageJump}
          startReviewAnswers={startReviewAnswers}
          testEnd={testEnd}
          forceEndTest={forceEndTest}
          fromDashboard={linkKind === "dashboard"}
          returnToDashboard={returnToDashboard}
          splitRatio={splitRatio}
          setSplitRatio={setSplitRatio}
          fontScale={fontScale}
        />
      );
    }
    // 總連結 → dashboard（三科卡片、依序解鎖）
    // 單科連結 → 原本的密碼頁，舊信裡的連結照樣能用
    if (linkKind === "dashboard") {
      return (
        <Dashboard
          handleLoading={handleLoading}
          urlKey={urlKey}
          savedPassword={dashboardPassword}
          onAuthenticated={setDashboardPassword}
        />
      );
    }
    return <PasswordInput handleLoading={handleLoading} urlKey={urlKey} />;
  };

  /**
   * 考完一科回到科目卡片頁。
   *
   * 不用 window.location.reload()——整頁重載會把密碼一起丟掉，學生得再輸一次，
   * 而且 pagehide 會被當成離開頁面。這裡只把「某一科的作答狀態」清乾淨，
   * Dashboard 會帶著記住的密碼重新抓一次科目狀態：剛考完的那科變成已完成、
   * 下一科才會解鎖。
   */
  const returnToDashboard = () => {
    setIsLoaded(false);
    setCandidate(null);
    setExamContent(null);
    setTestPages([]);
    setAnswers([]);
    setTestType("L");
    setTestTime(0);
    setPageType("Intro");
    setPageNumber(1);
    setPlayTestSound(false);
    setTestEnd(false);
    setLocked(null);
  };

  const handleLoading = (candidate, examContent) => {
    console.log(examContent.pages);
    // console.log(examContent.answers);
    examContent.pages.forEach((page, pageIndex) => {
      const formattedGroups = [];
      if (examContent.test_type === "W") {
        formattedGroups.push({
          type: "writing",
          number: pageIndex + 1,
          content: [
            {
              id: pageIndex + 1,
              answers: [],
            },
          ],
        });
      }

      page.groups.forEach((group) => {
        const questionList = [];
        let questionWording = "";

        const groupMatches = group.question_html.matchAll(questionReg);
        for (let match of groupMatches) {
          questionList.push(match[1]);
        }

        const groupMultiMatches = group.question_html.matchAll(questionsReg);
        for (let match of groupMultiMatches) {
          const startend = match[1].split("-");
          for (let i = parseInt(startend[0]); i <= parseInt(startend[1]); i++) {
            questionList.push(`${i}`);
          }
        }

        group.questions.forEach((question) => {
          if (!question.seq) {
            return;
          }

          if (question.seq.replaceAll(" ", "").match(/^\d+-\d+$/)) {
            const startend = question.seq.split("-");
            for (
              let i = parseInt(startend[0].replaceAll(" ", ""));
              i <= parseInt(startend[1].replaceAll(" ", ""));
              i++
            ) {
              questionList.push(`${i}`);
            }
          } else {
            questionList.push(question.seq);
          }
        });

        if (questionList.length === 0) {
          return;
        }

        questionList.sort((a, b) => a - b);
        questionList.forEach((questionSeq, index) => {
          if (index === 0) {
            questionWording += `${questionSeq}`;
            return;
          }

          if (questionList[index] - questionList[index - 1] === 1) {
            return;
          }
          questionWording += ` - ${questionList[index - 1]}, `;
          questionWording += `${questionList[index]}`;
        });
        if (
          questionWording.slice(
            questionList[questionList.length - 1].length * -1
          ) !== questionList[questionList.length - 1]
        ) {
          questionWording += ` - ${questionList[questionList.length - 1]}`;
        }

        // Adapt to original format
        const groupData = {
          question: `Questions ${questionWording}`,
          description: getGroupHint(group.type, group.question_limit),
          number: questionList.length,
          ...getGroupTypeMapping(group),
          ...parseGroupAnswerContent(group),
        };
        formattedGroups.push(groupData);
      });
      page.page = pageIndex + 1;
      page.formattedGroups = formattedGroups;

      // 題目內的拖拉答題
      const contentMatches = page.content.matchAll(questionReg);
      let pageContent = page.content;
      const parsedContent = [];
      for (let questionMatch of contentMatches) {
        const seq = parseInt(questionMatch[1]);
        const splitContent = pageContent.split(questionMatch[0]);
        parsedContent.push({
          id: seq,
          question: splitContent[0],
        });

        pageContent = splitContent.slice(1).join("");
      }
      if (parsedContent.length === 0) {
        return;
      }
      page.questionType = "drop in answer part";
      page.parsedContent = parsedContent;
      page.postContent = pageContent;
    });

    setCandidate(candidate);
    setExamContent(examContent);
    setTestPages(examContent.pages);
    setAnswers(parseAnswers(examContent.pages, examContent.answers));

    let testType = "L";
    let testTime = 30;
    switch (examContent.test_type) {
      case "L":
        break;
      case "R":
        testType = "R";
        testTime = 60;
        break;
      case "W":
        testType = "W";
        testTime = 60;
        break;
      default:
        throw new Error();
    }
    setTestType(testType);
    setTestTime(testTime);
    setIsLoaded(true);
  };

  const renderNextPage = () => {
    if (pageNumber === 1 && testType !== "L") {
      //skip TestSound component if not listening test
      setPageNumber(pageNumber + 2);
    } else {
      setPageNumber(pageNumber + 1);
    }

    if (pageNumber === 3) {
      setPageType("Tests");
    } else if (pageNumber === testPages.length + 3) {
      setPageType("End");
    }
  };

  const renderPreviousPage = () => {
    if (pageNumber === 4 && pageType === "Tests") {
      return;
    }
    setPageNumber(pageNumber - 1);
  };

  const TestsPageJump = (page) => {
    if (page) {
      setPageNumber(page);
    }
  };

  const forceEndTest = () => {
    setPageNumber(4);
    setPageType("End");
    setTestEnd(true);
  };

  const startReviewAnswers = () => {
    setPageNumber(4);
    setPageType("Tests");
  };

  const updatePlayTestSound = () => {
    setPlayTestSound(!playTestSound);
  };

  if (locked) {
    return <LockedNotice reason={locked.reason} />;
  }

  // 作答中整頁鎖成一個視窗高：內容區自己捲，題號列永遠看得到
  const inTest = isLoaded && pageType === "Tests" && !locked;

  return (
    <div className={`App${inTest ? " exam-fullscreen" : ""}`}>
      <Banner
        candidate={candidate}
        testType={testType}
        testTime={testTime}
        pageType={pageType}
        pageNumber={pageNumber}
        audioPath={examContent?.audio ?? ""}
        forceEndTest={forceEndTest}
        playTestSound={playTestSound}
        fontScale={fontScale}
        setFontScale={setFontScale}
      />
      {mainContent()}
    </div>
  );
};

export default App;
